import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { InlineAlert } from '../../components/ui/InlineAlert';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { EngagementIconButton } from '../../components/ui/EngagementIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PageShortcutLink } from '../../components/ui/PageShortcutLink';
import { ContractEngagementModal } from '../../components/commercial/ContractEngagementModal';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  Contract,
  ContractUpsertPayload,
  createContract,
  deleteContract,
  listContracts,
  updateContract,
} from '../../services/commercialContractsApi';
import { listCompanies, Company } from '../../services/coreCompaniesApi';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import {
  listTicketSlaOptions,
  ServiceTicketSlaOption,
} from '../../services/customerSuccessTicketsApi';
import { listEnterpriseCalendars, EnterpriseCalendarSummary } from '../../services/enterpriseCalendarsApi';
import { contractFieldHelp } from './contractFieldHelp';

const STATUS_LABELS: Record<string, string> = {
  Draft: 'Rascunho',
  Active: 'Ativo',
  Suspended: 'Suspenso',
  Expired: 'Expirado',
  Cancelled: 'Cancelado',
};

const emptyForm = (): ContractUpsertPayload => ({
  code: '',
  title: '',
  description: '',
  companyId: '',
  status: 'Draft',
  startDateUtc: new Date().toISOString().slice(0, 10),
  endDateUtc: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  welcomeEnabled: true,
  welcomeEmailTemplateId: null,
  csatEnabled: true,
  csatSurveyFormId: null,
  npsEnabled: true,
  npsSurveyFormId: null,
  npsFrequency: null,
});

function toDateInputValue(value: string): string {
  return value.slice(0, 10);
}

export function ContractsPage() {
  const { canWriteModule, canDeleteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('commercial.contracts');
  const canDelete = canDeleteModule('commercial.contracts');

  const [items, setItems] = useState<Contract[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ContractUpsertPayload>(emptyForm());
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [slaOptions, setSlaOptions] = useState<ServiceTicketSlaOption[]>([]);
  const [calendars, setCalendars] = useState<EnterpriseCalendarSummary[]>([]);
  const [engagementContract, setEngagementContract] = useState<Contract | null>(null);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadLookups(): Promise<void> {
    try {
      const [companyResult, productResult, slas, calendarResult] = await Promise.all([
        listCompanies({ activeOnly: true, pageSize: 200 }),
        listSalesProducts({ pageSize: 200 }),
        listTicketSlaOptions(),
        listEnterpriseCalendars({ pageSize: 200 }),
      ]);
      setCompanies(companyResult.items);
      setProducts(productResult.items);
      setSlaOptions(slas);
      setCalendars(calendarResult.items);
    } catch {
      toast.error('Falha ao carregar dados auxiliares.');
    }
  }

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listContracts({
        page: targetPage,
        pageSize,
        search: search || undefined,
        activeOnly,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar contratos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeOnly]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm());
  }

  function startEdit(contract: Contract): void {
    expandForm();
    setEditingId(contract.id);
    setForm({
      code: contract.code,
      title: contract.title,
      description: contract.description ?? '',
      companyId: contract.companyId,
      salesProductId: contract.salesProductId ?? undefined,
      status: contract.status,
      startDateUtc: toDateInputValue(contract.startDateUtc),
      endDateUtc: toDateInputValue(contract.endDateUtc),
      responseSlaId: contract.responseSlaId ?? undefined,
      resolutionSlaId: contract.resolutionSlaId ?? undefined,
      calendarId: contract.calendarId ?? undefined,
      ownerUserId: contract.ownerUserId ?? undefined,
      amount: contract.amount ?? undefined,
      notes: contract.notes ?? '',
      welcomeEnabled: contract.welcomeEnabled ?? true,
      welcomeEmailTemplateId: contract.welcomeEmailTemplateId ?? null,
      csatEnabled: contract.csatEnabled ?? true,
      csatSurveyFormId: contract.csatSurveyFormId ?? null,
      npsEnabled: contract.npsEnabled ?? true,
      npsSurveyFormId: contract.npsSurveyFormId ?? null,
      npsFrequency: contract.npsFrequency ?? null,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !form.companyId) return;

    setSaving(true);
    try {
      const payload: ContractUpsertPayload = {
        ...form,
        startDateUtc: new Date(`${form.startDateUtc}T00:00:00.000Z`).toISOString(),
        endDateUtc: new Date(`${form.endDateUtc}T23:59:59.000Z`).toISOString(),
      };

      if (editingId) {
        await updateContract(editingId, payload);
        toast.success('Contrato atualizado.');
        collapseForm(resetForm);
      } else {
        await createContract(payload);
        toast.success('Contrato criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar contrato.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(contract: Contract): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Excluir contrato',
        message: `Excluir "${contract.title}"?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteContract(contract.id);
      toast.success('Contrato excluído.');
      if (editingId === contract.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir contrato.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Contratos"
        description="Contratos comerciais vinculados a clientes — definem SLAs e escopo de atendimento."
        actions={
          <PageShortcutLink to="/commercial/projects" prefix="Acesso rápido">
            Projetos
          </PageShortcutLink>
        }
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar contrato' : 'Novo contrato'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.title || 'contrato selecionado'} — o formulário abriu em uma janela` : undefined
          }
          createSummary="Clique para cadastrar um novo contrato"
          isEditing={!!editingId}
          panelClassName="w-full max-w-5xl"
        >
          <form className="px-4 pb-4 pt-3" onSubmit={(event) => void handleSubmit(event)}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.company} required>
                Empresa
              </FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                required
                value={form.companyId}
                onChange={(e) => setForm({ ...form, companyId: e.target.value })}
              >
                <option value="">Selecione...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.title} required>
                Título
              </FieldLabel>
              <input
                className="erp-input mt-1 w-full"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.code}>Código</FieldLabel>
              <input
                className="erp-input mt-1 w-full"
                value={form.code ?? ''}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="Gerado automaticamente se vazio"
              />
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.status} required>
                Status
              </FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.startDate} required>
                Início
              </FieldLabel>
              <input
                type="date"
                className="erp-input mt-1 w-full"
                required
                value={form.startDateUtc}
                onChange={(e) => setForm({ ...form, startDateUtc: e.target.value })}
              />
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.endDate} required>
                Término
              </FieldLabel>
              <input
                type="date"
                className="erp-input mt-1 w-full"
                required
                value={form.endDateUtc}
                onChange={(e) => setForm({ ...form, endDateUtc: e.target.value })}
              />
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.responseSla}>SLA resposta</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                value={form.responseSlaId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, responseSlaId: e.target.value || undefined })
                }
              >
                <option value="">—</option>
                {slaOptions.map((sla) => (
                  <option key={sla.id} value={sla.id}>
                    {sla.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.resolutionSla}>SLA resolução</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                value={form.resolutionSlaId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, resolutionSlaId: e.target.value || undefined })
                }
              >
                <option value="">—</option>
                {slaOptions.map((sla) => (
                  <option key={sla.id} value={sla.id}>
                    {sla.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.product}>Produto</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                value={form.salesProductId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, salesProductId: e.target.value || undefined })
                }
              >
                <option value="">—</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block">
              <FieldLabel {...contractFieldHelp.calendar}>Calendário</FieldLabel>
              <select
                className="erp-input mt-1 w-full"
                value={form.calendarId ?? ''}
                onChange={(e) => setForm({ ...form, calendarId: e.target.value || undefined })}
              >
                <option value="">Padrão</option>
                {calendars.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block md:col-span-2 lg:col-span-3">
              <FieldLabel {...contractFieldHelp.description}>Descrição</FieldLabel>
              <textarea
                className="erp-input mt-1 w-full"
                rows={2}
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>

            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions md:col-span-2 lg:col-span-3"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </div>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="block min-w-[18rem]">
          <span className="erp-label">Buscar</span>
          <input
            className="erp-input mt-1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Título, código ou empresa"
          />
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
          />
          Somente vigentes
        </label>
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Filtrar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando contratos..." />
      ) : items.length === 0 ? (
        <InlineAlert>
          Nenhum contrato encontrado. {canWrite ? 'Use Novo contrato acima para cadastrar o primeiro.' : ''}
        </InlineAlert>
      ) : (
        <>
          <div className="erp-card overflow-x-auto">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Empresa</th>
                  <th>Título</th>
                  <th>Status</th>
                  <th>Vigência</th>
                  <th>SLAs</th>
                  <th className="w-48">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="whitespace-nowrap font-mono text-sm">{item.contractCode}</td>
                    <td>{item.companyName}</td>
                    <td>{item.title}</td>
                    <td>
                      <CodedStatusBadge
                        status={item.status}
                        label={STATUS_LABELS[item.status] ?? item.status}
                      />
                      {item.daysUntilEnd != null && item.daysUntilEnd <= 30 ? (
                        <span className="ml-2 text-xs text-amber-700">
                          ({item.daysUntilEnd}d)
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap text-sm">
                      {new Date(item.startDateUtc).toLocaleDateString('pt-BR')} —{' '}
                      {new Date(item.endDateUtc).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="text-sm">
                      {item.responseSlaName ?? '—'} / {item.resolutionSlaName ?? '—'}
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <PageShortcutLink
                          to={`/commercial/projects?companyId=${item.companyId}&contractId=${item.id}`}
                        >
                          Projetos
                        </PageShortcutLink>
                        <EngagementIconButton onClick={() => setEngagementContract(item)} />
                        {canWrite ? <EditIconButton onClick={() => startEdit(item)} /> : null}
                        {canDelete ? <DeleteIconButton onClick={() => void handleDelete(item)} /> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          />
        </>
      )}

      <ContractEngagementModal
        open={engagementContract != null}
        contract={engagementContract}
        canWrite={canWrite}
        onClose={() => setEngagementContract(null)}
        onSaved={(updated) => {
          setItems((current) =>
            current.map((item) => (item.id === updated.id ? updated : item)),
          );
          setEngagementContract(null);
        }}
      />
    </PageShell>
  );
}
